import React from 'react';
import { ArrowRight } from 'lucide-react';

interface RoastWeightCellProps {
  inputWeight: number | null | undefined;
  outputWeight: number | null | undefined;
  shrinkageKg: number | null | undefined;
  shrinkagePercent: number | null | undefined;
  showShrinkage: boolean;
  formatNumber: (value: number | null | undefined, digits?: number) => string | null;
  formatPercent: (value: number | null | undefined) => string | null;
}

/**
 * Displays roast input → output weight with shrinkage info centered
 * beneath the weight row, while remaining left-aligned within its cell.
 *
 * Layout:
 *   30.000 → 26.000          ← left-aligned in cell
 *   -4.000kg (13.333%)       ← centered relative to the weight row above
 */
export const RoastWeightCell: React.FC<RoastWeightCellProps> = ({
  inputWeight,
  outputWeight,
  shrinkageKg,
  shrinkagePercent,
  showShrinkage,
  formatNumber,
  formatPercent,
}) => {
  const hasData =
    formatNumber(inputWeight) !== null || formatNumber(outputWeight) !== null;

  if (!hasData) {
    return <span className="text-gray-300">—</span>;
  }

  return (
    /**
     * `inline-flex flex-col items-center`:
     *   - `inline-flex` → wrapper shrinks to content width → stays left in the td
     *   - `items-center` → shrinkage row is centered relative to the weight row
     *     (not relative to the full cell width)
     */
    <div className="inline-flex flex-col items-center leading-tight">
      {/* Weight row */}
      <div className="flex items-center gap-2 tabular-nums text-sm">
        <span className="text-gray-500">
          {formatNumber(inputWeight) ?? '—'}
        </span>
        <ArrowRight className="h-3.5 w-3.5 text-gray-900 shrink-0" />
        <span className="font-semibold text-gray-900">
          {formatNumber(outputWeight) ?? '—'}
        </span>
      </div>

      {/* Shrinkage — centered beneath weight row */}
      {showShrinkage && (
        <span
          className="text-[11px] text-primary-700 font-semibold mt-1 tabular-nums"
          data-test-id="roast-weight-shrinkage"
        >
          -{formatNumber(shrinkageKg)}kg ({formatPercent(shrinkagePercent)}%)
        </span>
      )}
    </div>
  );
};
